import { cloneElement, useState } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { Dropdown } from "semantic-ui-react";
import { readEml } from "eml-parse-js";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import CampaignService from "services/Campaign";
import EmailHtmlFrame from "components/email/EmailHtmlFrame";

// readEml reports through a callback, so it is wrapped for the query to await.
const parseEmlHtml = raw =>
  new Promise((resolve, reject) =>
    readEml(raw, (err, eml) => (err ? reject(err) : resolve(eml.html)))
  );

const fetchEmailHtml = async (emailUrl, emailId) => {
  if (emailUrl) {
    const { data } = await axios.get(emailUrl);
    return parseEmlHtml(data);
  }
  const data = await CampaignService.getEmailData(emailId);
  return data.html_content;
};

const ViewEmailModal = ({ emailId, emailUrl, dropdown = false, trigger }) => {
  const [open, setOpen] = useState(false);

  // A sent email does not change, so a loaded body is reused on every later
  // open instead of being fetched again.
  const { data: htmlContent, isError: hasEmailError } = useQuery({
    queryKey: ["email-content", emailUrl ?? emailId],
    queryFn: () => fetchEmailHtml(emailUrl, emailId),
    enabled: open && Boolean(emailUrl || emailId),
    staleTime: Infinity,
    retry: false,
  });

  // The notices table lists every row, including ones with no stored email, so
  // its menu item is always shown. The standalone button is not, because there
  // would be nothing to open.
  if (!dropdown && !trigger && !emailUrl && !emailId) {
    return null;
  }

  const defaultTrigger = dropdown ? (
    <Dropdown.Item content="View Email Sent" />
  ) : (
    <Button variant="outline" size="sm">
      View Email Sent
    </Button>
  );

  return (
    <>
      {cloneElement(trigger ?? defaultTrigger, {
        onClick: () => setOpen(true),
      })}
      <Dialog open={open} onOpenChange={setOpen} disablePointerDismissal>
        <DialogContent className="tw:sm:max-w-4xl">
          <DialogTitle>Email Content</DialogTitle>
          {hasEmailError ? (
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>Could not load email</AlertDescription>
            </Alert>
          ) : htmlContent ? (
            <EmailHtmlFrame html={htmlContent} title="Email content" />
          ) : (
            <div className="tw:flex tw:items-center tw:gap-2 tw:text-(--color-text-secondary)">
              <Spinner />
              <span>Loading the email…</span>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ViewEmailModal;
